import type { DnsRecord } from '../../api/types'
import { t } from '../../i18n/lingua'

/** The record types CENSO can write (the same list as the Control Plane). */
export const TIPI_SCRIVIBILI = ['A', 'AAAA', 'CNAME', 'MX', 'NS', 'PTR', 'SRV', 'TXT']

/** What a record type is for, in one line (shown in the type picker). */
export function descrizioneTipo(tipo: string): string {
  switch (tipo) {
    case 'A': return t('Nome → indirizzo IPv4')
    case 'AAAA': return t('Nome → indirizzo IPv6')
    case 'CNAME': return t('Alias di un altro nome')
    case 'MX': return t('Server di posta del dominio')
    case 'NS': return t('Server dei nomi (delega)')
    case 'PTR': return t('Indirizzo → nome (zona inversa)')
    case 'SRV': return t('Servizio: priorità, peso, porta e destinazione')
    case 'TXT': return t('Testo (SPF, verifiche di dominio…)')
    default: return ''
  }
}

const senzaPunto = (v: string) => v.trim().replace(/\.$/, '')

/** The record name relative to the zone: «www.esempio.it» → «www», the zone itself → «@». */
export function nomeRelativo(nome: string | null | undefined, zona: string): string {
  const n = senzaPunto(nome ?? '')
  const z = senzaPunto(zona).toLowerCase()
  if (!n || n === '@' || n.toLowerCase() === z) return '@'
  if (n.toLowerCase().endsWith('.' + z)) return n.slice(0, n.length - z.length - 1)
  return n
}

/** The absolute name (without the trailing dot) of a name typed relative to the zone or in full. */
export function nomeAssoluto(nome: string, zona: string): string {
  const n = senzaPunto(nome)
  const z = senzaPunto(zona)
  if (!n || n === '@') return z
  if (n.toLowerCase() === z.toLowerCase() || n.toLowerCase().endsWith('.' + z.toLowerCase())) return n
  return `${n}.${z}`
}

/** The TTL presets offered in the editor (seconds). */
export const TTL_PRESET = [300, 900, 3600, 14400, 86400]

/** A TTL a person reads: 300 → «5 min», 3600 → «1 h», 86400 → «1 g». */
export function formatTtl(secondi: number | null | undefined): string {
  if (secondi == null) return '—'
  if (secondi % 86400 === 0) return t('{n} g', { n: secondi / 86400 })
  if (secondi % 3600 === 0) return t('{n} h', { n: secondi / 3600 })
  if (secondi % 60 === 0) return t('{n} min', { n: secondi / 60 })
  return t('{n} s', { n: secondi })
}

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/

/** The 32 hex nibbles of an IPv6 address, or null if it is not one. */
function nibbleIPv6(ip: string): string | null {
  const v = ip.trim().toLowerCase()
  if (!/^[0-9a-f:]+$/.test(v) || (v.match(/::/g) ?? []).length > 1) return null
  const [testa, coda] = v.includes('::') ? v.split('::') : [v, null]
  const a = testa ? testa.split(':') : []
  const b = coda != null ? (coda ? coda.split(':') : []) : []
  const mancanti = 8 - a.length - b.length
  if (coda == null ? a.length !== 8 : mancanti < 1) return null
  const gruppi = [...a, ...Array(coda == null ? 0 : mancanti).fill('0'), ...b]
  if (gruppi.some((g) => !/^[0-9a-f]{1,4}$/.test(g))) return null
  return gruppi.map((g) => g.padStart(4, '0')).join('')
}

export function ipValido(v: string, versione: 4 | 6): boolean {
  return versione === 4 ? IPV4.test(v.trim()) : nibbleIPv6(v) !== null
}

const NOME_HOST = /^[A-Za-z0-9_]([A-Za-z0-9_.-]{0,252})\.?$/
export function nomeHostValido(v: string): boolean {
  return NOME_HOST.test(v.trim())
}

const NOME_RECORD = /^(@|\*|(\*\.)?[A-Za-z0-9_][A-Za-z0-9_.-]{0,252})$/
export function nomeRecordValido(v: string): boolean {
  return NOME_RECORD.test(senzaPunto(v))
}

/** Whether a zone is a reverse lookup zone. */
export function zonaInversa(zona: string): boolean {
  const z = senzaPunto(zona).toLowerCase()
  return z.endsWith('.in-addr.arpa') || z.endsWith('.ip6.arpa')
}

/** The reverse name of an address («10.0.0.5» → «5.0.0.10.in-addr.arpa»), or null. */
export function puntatore(ip: string): string | null {
  const v = ip.trim()
  if (IPV4.test(v)) return `${v.split('.').reverse().join('.')}.in-addr.arpa`
  const n = nibbleIPv6(v)
  return n ? `${n.split('').reverse().join('.')}.ip6.arpa` : null
}

/** The PTR name relative to a reverse zone for an address («5» in 0.0.10.in-addr.arpa), or null if outside the zone. */
export function nomePtrDaIp(ip: string, zona: string): string | null {
  const p = puntatore(ip)
  const z = senzaPunto(zona).toLowerCase()
  if (!p) return null
  if (p === z) return '@'
  return p.endsWith('.' + z) ? p.slice(0, p.length - z.length - 1) : null
}

/** The zone's own records (SOA, NS at the apex): hidden by default, nobody edits them every day. */
export function diSistema(r: DnsRecord, zona: string): boolean {
  if (r.type === 'SOA') return true
  return r.type === 'NS' && nomeRelativo(r.name, zona) === '@'
}

/** A dynamic record that has not been refreshed for `giorni` days: a client that no longer registers. */
export function registrazioneVecchia(ts: string | null | undefined, giorni = 30): boolean {
  if (!ts) return false
  const quando = Date.parse(ts)
  return Number.isFinite(quando) && Date.now() - quando > giorni * 86400_000
}

/** The fields of a record value, split per type (to edit them one by one). */
export interface CampiRecord {
  ip: string
  bersaglio: string
  priorita: string
  peso: string
  porta: string
  testo: string
}

export const CAMPI_VUOTI: CampiRecord = { ip: '', bersaglio: '', priorita: '10', peso: '100', porta: '', testo: '' }

/** A record value split into its fields (the reverse of `componi`). */
export function scomponi(tipo: string, contenuto: string | null | undefined): CampiRecord {
  const v = (contenuto ?? '').trim()
  const parti = v.split(/\s+/)
  switch (tipo) {
    case 'A':
    case 'AAAA':
      return { ...CAMPI_VUOTI, ip: v }
    case 'CNAME':
    case 'NS':
    case 'PTR':
      return { ...CAMPI_VUOTI, bersaglio: senzaPunto(v) }
    case 'MX':
      return { ...CAMPI_VUOTI, priorita: parti[0] ?? '10', bersaglio: senzaPunto(parti[1] ?? '') }
    case 'SRV':
      return { ...CAMPI_VUOTI, priorita: parti[0] ?? '0', peso: parti[1] ?? '100', porta: parti[2] ?? '', bersaglio: senzaPunto(parti[3] ?? '') }
    default:
      return { ...CAMPI_VUOTI, testo: v.replace(/^"(.*)"$/, '$1') }
  }
}

/** The record value from its fields, in the form the Control Plane validates. */
export function componi(tipo: string, c: CampiRecord): string {
  switch (tipo) {
    case 'A':
    case 'AAAA':
      return c.ip.trim()
    case 'CNAME':
    case 'NS':
    case 'PTR':
      return senzaPunto(c.bersaglio)
    case 'MX':
      return `${c.priorita.trim()} ${senzaPunto(c.bersaglio)}`
    case 'SRV':
      return `${c.priorita.trim()} ${c.peso.trim()} ${c.porta.trim()} ${senzaPunto(c.bersaglio)}`
    default:
      return c.testo.trim()
  }
}

const intero = (v: string, max = 65535) => /^\d+$/.test(v.trim()) && Number(v) <= max

/** What is wrong with the fields of a record, in words; empty when it can be written. */
export function problemi(tipo: string, nome: string, c: CampiRecord, ttl: number): string[] {
  const fuori: string[] = []
  if (!nomeRecordValido(nome || '@')) fuori.push(t('Il nome contiene caratteri che il DNS non accetta.'))
  if (!Number.isInteger(ttl) || ttl < 1) fuori.push(t('Il TTL deve essere un numero di secondi.'))
  switch (tipo) {
    case 'A':
      if (!ipValido(c.ip, 4)) fuori.push(t('Serve un indirizzo IPv4, per esempio 10.0.0.5.'))
      break
    case 'AAAA':
      if (!ipValido(c.ip, 6)) fuori.push(t('Serve un indirizzo IPv6, per esempio 2001:db8::5.'))
      break
    case 'CNAME':
    case 'NS':
    case 'PTR':
      if (!nomeHostValido(c.bersaglio)) fuori.push(t('Serve un nome host, per esempio server1.esempio.it.'))
      break
    case 'MX':
      if (!intero(c.priorita)) fuori.push(t('La priorità è un numero da 0 a 65535.'))
      if (!nomeHostValido(c.bersaglio)) fuori.push(t('Serve il nome del server di posta.'))
      break
    case 'SRV':
      if (!intero(c.priorita) || !intero(c.peso) || !intero(c.porta)) fuori.push(t('Priorità, peso e porta sono numeri da 0 a 65535.'))
      if (!nomeHostValido(c.bersaglio)) fuori.push(t('Serve il nome host di destinazione.'))
      break
    case 'TXT':
      if (!c.testo.trim()) fuori.push(t('Il testo è vuoto.'))
      else if (/[\r\n]/.test(c.testo)) fuori.push(t('Il testo deve stare su una riga sola.'))
      break
  }
  return fuori
}

/**
 * The network a reverse zone stands for, as people write it:
 * «0.0.10.in-addr.arpa» → «10.0.0.0/24», «8.b.d.0.1.0.0.2.ip6.arpa» →
 * «2001:db8::/32». Null for a forward zone (or a classless delegation).
 */
export function reteZonaInversa(zona: string | null | undefined): string | null {
  const z = senzaPunto(zona ?? '').toLowerCase()
  if (z.endsWith('.in-addr.arpa')) {
    const ottetti = z.slice(0, -'.in-addr.arpa'.length).split('.').reverse()
    if (!ottetti.length || ottetti.length > 4 || ottetti.some((o) => !/^\d{1,3}$/.test(o) || Number(o) > 255)) return null
    return `${[...ottetti, '0', '0', '0', '0'].slice(0, 4).join('.')}/${ottetti.length * 8}`
  }
  if (z.endsWith('.ip6.arpa')) {
    const nibble = z.slice(0, -'.ip6.arpa'.length).split('.').reverse()
    if (!nibble.length || nibble.length > 32 || nibble.some((n) => !/^[0-9a-f]$/.test(n))) return null
    const esadecimale = nibble.join('').padEnd(32, '0')
    const gruppi = Array.from({ length: 8 }, (_, i) => esadecimale.slice(i * 4, i * 4 + 4).replace(/^0+(?=.)/, ''))
    // The longest run of zero groups becomes «::».
    let inizio = -1
    let lungo = 0
    for (let i = 0; i < 8; i += 1) {
      let j = i
      while (j < 8 && gruppi[j] === '0') j += 1
      if (j - i > lungo && j - i > 1) { inizio = i; lungo = j - i }
      i = j > i ? j - 1 : i
    }
    const testo = inizio < 0
      ? gruppi.join(':')
      : `${gruppi.slice(0, inizio).join(':')}::${gruppi.slice(inizio + lungo).join(':')}`
    return `${testo}/${nibble.length * 4}`
  }
  return null
}

/** A sort key for zones: reverse zones by network (numerically), forward ones by name. */
export function chiaveZona(zona: string | null | undefined): string {
  const z = senzaPunto(zona ?? '').toLowerCase()
  if (z.endsWith('.in-addr.arpa')) {
    const ottetti = z.slice(0, -'.in-addr.arpa'.length).split('.').reverse()
    return `4:${ottetti.map((o) => o.padStart(3, '0')).join('.')}`
  }
  if (z.endsWith('.ip6.arpa')) return `6:${z.split('.').reverse().join('')}`
  return `0:${z}`
}
